import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StateKey, TransferState, makeStateKey } from '@angular/platform-browser';

import { Observable, of, tap } from 'rxjs';

import { PlatformService } from '../platform/platform.service';

import { TransferHttpResponse, createHttpResponse, generateCacheKey, serializeHttpResponse } from './http.helpers';

export function generateStateKey(request: HttpRequest<unknown>): StateKey<TransferHttpResponse> {
  return makeStateKey<TransferHttpResponse>(generateCacheKey(request));
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 *
 * Based on @nguniversal TransferHttpCacheInterceptor, but adapted to not skip GraphQL calls.
 *
 * Transfers cached HTTP calls from the server to the client application,
 * used in conjunction with `ServerTransferHttpCacheModule` (client/server)
 */

@Injectable()
export class HttpTransferStateInterceptor implements HttpInterceptor {
  constructor(private readonly transferState: TransferState, private readonly platform: PlatformService) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!['GET', 'HEAD'].includes(req.method)) {
      // Pass the request through if no GET/HEAD.
      return next.handle(req);
    }

    const storeKey = generateStateKey(req);

    if (this.transferState.hasKey(storeKey)) {
      // Request found in transfer-state. Respond using it.
      return of(createHttpResponse(this.transferState.get(storeKey, {})));
    } else {
      // Request not found in cache. Make the request and add it to transfer-state if on server.
      const next$ = next.handle(req);

      return this.platform.isServer
        ? next$.pipe(
            tap((event: HttpEvent<unknown>) => {
              if (event instanceof HttpResponse) {
                this.transferState.set<TransferHttpResponse>(storeKey, serializeHttpResponse(event, false));
              }
            })
          )
        : next$;
    }
  }
}
